import { HttpClient } from '@angular/common/http';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import { Observable, concat, map, tap, toArray } from 'rxjs';
import { first, switchMap } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { MessageService, SelectItem } from 'primeng/api';

import { ApiKeyInfoOutput } from '@digitalforest/ngx-oak-api';

import { selectFirstValidApiKeyInfo, selectValidApiKeyInfo } from './store/api-key-info.selector';
import { ScopeState } from './store/scope.feature';
import { selectFirstValidScopeState, selectValidScopeState } from './store/scope.selector';

/**
 * TinyMCE common configuration
 */
export const TINYMCE_PLUGIN_LIST = [
  'anchor',
  'autolink',
  'charmap',
  'codesample',
  'emoticons',
  'image',
  'link',
  'lists',
  'media',
  'searchreplace',
  'table',
  'visualblocks',
  'wordcount',
  'code',
];

export const TINYMCE_TOOLBAR_LIST = [
  'undo',
  'redo',
  '|',
  'blocks',
  'fontfamily',
  'fontsize',
  '|',
  'code',
  'bold',
  'italic',
  'underline',
  'strikethrough',
  '|',
  'forecolor',
  'backcolor',
  '|',
  'link',
  'image',
  'media',
  'table',
  '|',
  'align',
  'lineheight',
  'checklist',
  'numlist',
  'bullist',
  'indent',
  'outdent',
  '|',
  'emoticons',
  'charmap',
  'removeformat',
];

export const TINYMCE_DEFAULT_CONFIG = {
  plugins: TINYMCE_PLUGIN_LIST.join(' '),
  toolbar: TINYMCE_TOOLBAR_LIST.join(' '),
  menubar: false,
  max_height: 500,
  min_height: 200,
  base_url: '/tinymce',
  suffix: '.min',
};

export const languageCodeOptions: SelectItem[] = [
  { value: 'en', label: 'English' },
  { value: 'it', label: 'Italiano' },
  { value: 'es', label: 'Español' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'ar', label: 'العربية' },
];

export enum OakRoles {
  SUPER_ADMIN = 0,
  ADMIN = 1,
  BACK_OFFICE = 2,
  PARTICIPANT = 3,
  HOSTESS = 4,
  PENDING = 5,
  BLIND_ADMIN = 6,
  SPEAKER = 7,
  MEMBER = 8,
  FINANCING_MEMBER = 9,
  GUEST = 10,
  LEAD = 11,
  TRANSIENT = 12,
}

export enum OakGenders {
  MALE = 0,
  FEMALE = 1,
  NON_BINARY = 2,
  OTHER = 3,
}

export enum OakEventCategories {
  CONFERENCE = 0,
  WEBINAR = 1,
  TRAINING = 2,
  STREAMING = 3,
  VIDEO_ON_DEMAND = 4,
}

export enum OakFeedbackAnswerType {
  MULTIPLE_CHOICE = 0,
  FREE_FORMAT = 1,
  LIST = 2,
}

export const OakRolesOptions: SelectItem[] = [
  { value: OakRoles.SUPER_ADMIN, label: 'Super Admin' },
  { value: OakRoles.ADMIN, label: 'Admin' },
  { value: OakRoles.BACK_OFFICE, label: 'Back Office' },
  { value: OakRoles.PARTICIPANT, label: 'Participant' },
  { value: OakRoles.HOSTESS, label: 'Hostess' },
  { value: OakRoles.PENDING, label: 'Pending' },
  { value: OakRoles.BLIND_ADMIN, label: 'Blind Admin' },
  { value: OakRoles.SPEAKER, label: 'Speaker' },
  { value: OakRoles.MEMBER, label: 'Member' },
  { value: OakRoles.FINANCING_MEMBER, label: 'Financing Member' },
  { value: OakRoles.GUEST, label: 'Guest' },
  { value: OakRoles.LEAD, label: 'Lead' },
  { value: OakRoles.TRANSIENT, label: 'Transient' },
];

export const OakGendersOptions: SelectItem[] = [
  { value: OakGenders.MALE, label: 'Male' },
  { value: OakGenders.FEMALE, label: 'Female' },
];

export const OakEventCategoriesOptions: SelectItem[] = [
  { value: OakEventCategories.CONFERENCE, label: 'Conference' },
  { value: OakEventCategories.WEBINAR, label: 'Webinar' },
  { value: OakEventCategories.TRAINING, label: 'Training' },
  { value: OakEventCategories.STREAMING, label: 'Streaming' },
  { value: OakEventCategories.VIDEO_ON_DEMAND, label: 'Video on Demand' },
];

export const OakFeedbackAnswerTypeOptions: SelectItem[] = [
  { value: OakFeedbackAnswerType.MULTIPLE_CHOICE, label: 'Multiple Choice' },
  { value: OakFeedbackAnswerType.FREE_FORMAT, label: 'Free Format' },
  { value: OakFeedbackAnswerType.LIST, label: 'List' },
];

export function operationSuccessful(messageService: MessageService, summary?: string): void {
  if (!summary) {
    summary = 'Operation successful';
  }

  messageService.add({
    severity: 'success',
    summary: summary,
  });
}

export function operationFailed(error: unknown, messageService: MessageService, summary?: string): void {
  if (error) {
    console.error(error);
  }

  if (!summary) {
    summary = 'Operation failed';
  }

  messageService.add({
    severity: 'error',
    summary: summary,
  });
}

export function showCreateRecordNotification(recordName: string, messageService: MessageService): void {
  operationSuccessful(messageService, `${recordName} added`);
}

export function showDeleteRecordNotification(recordName: string, messageService: MessageService): void {
  operationSuccessful(messageService, `${recordName} deleted`);
}

export function showDeleteRecordFailedNotification(recordName: string, messageService: MessageService, summary: string = undefined): void {
  if (!summary) {
    summary = `Unable to delete ${recordName}`;
  }

  operationFailed(null, messageService, summary);
}

export function showUpdateRecordNotification(recordName: string, messageService: MessageService): void {
  operationSuccessful(messageService, `${recordName} updated`);
}

export function showDuplicateRecordNotification(recordName: string, messageService: MessageService): void {
  operationSuccessful(messageService, `${recordName} duplicated`);
}

export function manageAssociations<ItemType, IdType>({
  sourceItemList,
  targetItemList,
  getId,
  setId,
  createMethod,
  deleteMethod,
  updateMethod,
}: {
  sourceItemList: ItemType[];
  targetItemList: ItemType[];
  getId: (item: ItemType) => IdType;
  setId: (item: ItemType, id: IdType) => void;
  createMethod: (item: ItemType) => Observable<ItemType>;
  deleteMethod: (id: IdType) => Observable<unknown>;
  updateMethod?: (id: IdType, item: ItemType) => Observable<ItemType>;
}): Observable<unknown> {
  const deleteTargetList = sourceItemList.map((item, index) => ({ index, item })).filter(({ item }) => getId(item) !== null);
  const createTargetList = targetItemList.map((item, index) => ({ index, item })).filter(({ item }) => getId(item) === null);
  const updateTargetList = targetItemList.map((item, index) => ({ index, item })).filter(({ item }) => getId(item) !== null);

  const deleteObservableList$ = deleteTargetList.map(({ item, index }) => {
    console.debug('[manageAssociations] deleteObservableList$: item = ', item);
    return deleteMethod(getId(item)).pipe(tap(() => setId(sourceItemList[index], null)));
  });

  const createObservableList$ = createTargetList.map(({ item, index }) => {
    console.debug('[manageAssociations] createObservableList$: item = ', item);
    return createMethod(item).pipe(tap((responseItem) => setId(targetItemList[index], getId(responseItem))));
  });

  const updateObservableList$ = updateTargetList.map(({ item, index }) => {
    console.debug('[manageAssociations] updateObservableList$: item = ', item);
    return updateMethod(getId(item), item).pipe(tap((responseItem) => setId(targetItemList[index], getId(responseItem))));
  });

  return concat(...deleteObservableList$, ...createObservableList$, ...updateObservableList$).pipe(
    toArray(),
    map((results) => results.filter((result) => result !== null)),
  );
}

export function withValidScopeState$<T>(store: Store, func: (scopeState: ScopeState) => Observable<T>): Observable<T> {
  return store.pipe(
    selectValidScopeState,
    switchMap((scopeState) => {
      return func(scopeState);
    }),
  );
}

export function withFirstValidScopeState$<T>(store: Store, func: (scopeState: ScopeState) => Observable<T>): Observable<T> {
  return store.pipe(
    selectFirstValidScopeState,
    switchMap((scopeState) => {
      return func(scopeState);
    }),
  );
}

export function withValidApiKeyInfo$<T>(store: Store, func: (apiKeyInfo: ApiKeyInfoOutput) => Observable<T>): Observable<T> {
  return store.pipe(
    selectValidApiKeyInfo,
    switchMap((apiKeyInfo) => {
      return func(apiKeyInfo);
    }),
  );
}

export function withFirstValidApiKeyInfo$<T>(store: Store, func: (apiKeyInfo: ApiKeyInfoOutput) => Observable<T>): Observable<T> {
  return store.pipe(
    selectFirstValidApiKeyInfo,
    switchMap((apiKeyInfo) => {
      return func(apiKeyInfo);
    }),
  );
}

export function downloadFile(httpClient: HttpClient, fullUrl: string, filename: string) {
  httpClient
    .get(fullUrl, { responseType: 'blob' })
    .pipe(
      map((response) => response as Blob),
      map((blob) => ({
        blob,
        download: filename,
      })),
      first(),
    )
    .subscribe(({ blob, download }) => {
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = download;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    });
}

export function getLoginRoute(): string[] {
  return ['/', 'auth', 'login'];
}

export function getMainRoute(): string[] {
  return ['/', 'main'];
}

export function getHomeRoute(): string[] {
  return ['/', 'main', 'home'];
}

export function getDashboardRoute(): string[] {
  return ['/', 'main', 'dashboard'];
}

export function getAdministrationTenantsRoute(): string[] {
  return ['/', 'main', 'administration', 'tenants', 'list'];
}

export function getAdministrationAdministratorsRoute(): string[] {
  return ['/', 'main', 'administration', 'administrators', 'list'];
}

export function getCrmUsersRoute(): string[] {
  return ['/', 'main', 'crm', 'users', 'list'];
}

export function getCrmUserRoute(userId: number): string[] {
  return ['/', 'main', 'crm', 'users', 'edit', userId.toString()];
}

export function getCrmUserCategoriesRoute(): string[] {
  return ['/', 'main', 'crm', 'user_categories', 'list'];
}

export function getCrmGroupsRoute(): string[] {
  return ['/', 'main', 'crm', 'groups', 'list'];
}

export function getCrmGroupUsersRoute(groupId: number): string[] {
  return ['/', 'main', 'crm', 'groups', groupId.toString(), 'group_users', 'list'];
}

export function getCrmFirmsRoute(): string[] {
  return ['/', 'main', 'crm', 'firms', 'list'];
}

export function getCmsEventsRoute(): string[] {
  return ['/', 'main', 'cms', 'events', 'list'];
}

export function getCmsEventCategoriesRoute(): string[] {
  return ['/', 'main', 'cms', 'event_categories', 'list'];
}

export function getCmsParticipantCategoriesRoute(): string[] {
  return ['/', 'main', 'cms', 'participant_categories', 'list'];
}

export function getCmsEventSubcategoriesRoute(): string[] {
  return ['/', 'main', 'cms', 'event_subcategories', 'list'];
}

export function getCmsNewsCategoriesRoute(): string[] {
  return ['/', 'main', 'cms', 'news_categories', 'list'];
}

export function getCmsAgendaCategoriesRoute(): string[] {
  return ['/', 'main', 'cms', 'agenda_categories', 'list'];
}

export function getCmsAgendaSubcategoriesRoute(): string[] {
  return ['/', 'main', 'cms', 'agenda_subcategories', 'list'];
}

export function getCmsVenueCategoriesRoute(): string[] {
  return ['/', 'main', 'cms', 'venue_categories', 'list'];
}

export function getCmsVenuesRoute(): string[] {
  return ['/', 'main', 'cms', 'venues', 'list'];
}

export function getCmsDeviceTokensRoute(): string[] {
  return ['/', 'main', 'cms', 'device_tokens', 'list'];
}

export function getCmsGigyaKeyDataRoute(): string[] {
  return ['/', 'main', 'cms', 'gigya_key_data', 'list'];
}

export function getCmsRegistrationFormsRoute(): string[] {
  return ['/', 'main', 'cms', 'registration_forms', 'list'];
}

export function getCmsSpeakersRoute(): string[] {
  return ['/', 'main', 'cms', 'speakers', 'list'];
}

export function getCmsSponsorsRoute(): string[] {
  return ['/', 'main', 'cms', 'sponsors', 'list'];
}

export function getCmsHotelsRoute(): string[] {
  return ['/', 'main', 'cms', 'hotels', 'list'];
}

export function getCmsCompanyFilesRoute(): string[] {
  return ['/', 'main', 'cms', 'company_files', 'list'];
}

export function getCmsMediaGalleryRoute(): string[] {
  return ['/', 'main', 'cms', 'media_gallery', 'list'];
}

export function getCmsMediaGalleryItemsRoute(mediaGalleryId: number): string[] {
  return ['/', 'main', 'cms', 'media_gallery', mediaGalleryId.toString(), 'media_gallery_items', 'list'];
}

export function getInteractivityChatGroupsRoute(): string[] {
  return ['/', 'main', 'interactivity', 'chat_groups', 'list'];
}

export function getInteractivityGamesRoute(): string[] {
  return ['/', 'main', 'interactivity', 'games'];
}

export function getInteractivityPopupsRoute(): string[] {
  return ['/', 'main', 'interactivity', 'popups'];
}

export function getReportsUserReportRoute(): string[] {
  return ['/', 'main', 'reports', 'user-report'];
}

export function getCommunicationMailerRoute(): string[] {
  return ['/', 'main', 'communication', 'mail'];
}

export function getCommunicationMailTemplatesRoute(): string[] {
  return ['/', 'main', 'communication', 'mail_templates', 'list'];
}

export function getCommunicationMailTemplateEditRoute(id: number): string[] {
  return ['/', 'main', 'communication', 'mail_templates', 'edit', id.toString()];
}

export function getCommunicationSentMailsRoute(): string[] {
  return ['/', 'main', 'communication', 'sent_mails', 'list'];
}

export function getCommunicationSentPushesRoute(): string[] {
  return ['/', 'main', 'communication', 'sent_pushes', 'list'];
}

export function getCommunicationPDFTemplatesRoute(): string[] {
  return ['/', 'main', 'communication', 'pdf_templates', 'list'];
}

export function getCommunicationPushRoute(): string[] {
  return ['/', 'main', 'communication', 'push'];
}

export function getCommunicationSmsRoute(): string[] {
  return ['/', 'main', 'communication', 'sms'];
}

export function getCommunicationWhatsappRoute(): string[] {
  return ['/', 'main', 'communication', 'whatsapp'];
}

export function getCommunicationHelpdeskRoute(): string[] {
  return ['/', 'main', 'communication', 'helpdesk'];
}

export function getSettingsCompanySettingsRoute(): string[] {
  return ['/', 'main', 'settings', 'company_settings'];
}

export function getSettingsBackendHelpRoute(): string[] {
  return ['/', 'main', 'settings', 'backend_help', 'list'];
}

export function getTenantThemeSettingsRoute(tenantId: number): string[] {
  return ['/', 'main', 'administration', 'tenants', 'theme_settings', tenantId.toString()];
}

export function getEventSettingsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'settings'];
}

export function getEventSetupEventUsersRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'event_users', 'list'];
}

export function getEventSetupExhibitorsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'exhibitors', 'list'];
}

export function getEventSetupAgendasRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'agendas', 'list'];
}

export function getEventSetupStreamsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'streams', 'list'];
}

export function getEventSetupAgendaUsersRoute(eventId: number, agendaId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'agendas', 'edit', agendaId.toString(), 'agenda_users', 'list'];
}

export function getEventSetupTradeShowSectorsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'trade_show_sectors', 'list'];
}

export function getEventSetupMerchandiseCategoriesRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'merchandise_categories', 'list'];
}

export function getEventSetupFieldsOfApplicationRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'fields_of_application', 'list'];
}

export function getEventSetupRestaurantsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'restaurants', 'list'];
}

export function getEventSetupShuttleSchedulesRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'shuttle_schedules', 'list'];
}

export function getEventSetupFlightReservationsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'flight_reservations', 'list'];
}

export function getEventSetupCarReservationsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'car_reservations', 'list'];
}

export function getEventSetupHotelReservationsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'hotel_reservations', 'list'];
}

export function getEventSetupEventHotelRoomsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'event_hotel_rooms', 'list'];
}

export function getEventSetupTrainReservationsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'train_reservations', 'list'];
}

export function getEventSetupDocumentsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'documents', 'list'];
}

export function getEventSetupUserDocumentsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'user_documents', 'list'];
}

export function getEventSetupUserGeneratedContentBinsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'user_generated_content_bins', 'list'];
}

export function getEventSetupFeedbacksRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'feedbacks', 'list'];
}

export function getEventSetupFeedbackQuestionsRoute(eventId: number, feedbackId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'feedbacks', feedbackId.toString(), 'feedback_questions', 'list'];
}

export function getEventSetupPollsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'polls', 'list'];
}

export function getEventSetupExhibitorBrandsRoute(eventId: number, exhibitorId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'exhibitors', exhibitorId.toString(), 'exhibitor_brands', 'list'];
}

export function getEventSetupExhibitorProductsRoute(eventId: number, exhibitorId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'exhibitors', exhibitorId.toString(), 'exhibitor_products', 'list'];
}

export function getEventSetupExhibitorPressReleasesRoute(eventId: number, exhibitorId: number): string[] {
  return [
    '/',
    'main',
    'cms',
    'events',
    'setup',
    eventId.toString(),
    'exhibitors',
    exhibitorId.toString(),
    'exhibitor_press_releases',
    'list',
  ];
}

export function getEventSetupExhibitorVideosRoute(eventId: number, exhibitorId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'exhibitors', exhibitorId.toString(), 'exhibitor_videos', 'list'];
}

export function getEventSetupExhibitorDocumentsRoute(eventId: number, exhibitorId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'exhibitors', exhibitorId.toString(), 'exhibitor_documents', 'list'];
}

export function getEventSetupEventSponsorTiersRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'event_sponsor_tiers', 'list'];
}

export function getEventSetupMenuBuilderRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'menu_builder'];
}

export function getEventSetupMeetingsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'meetings', 'list'];
}

export function getEventSetupLiveChatsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'livechats', 'list'];
}

export function getEventSetupWallsRoute(eventId: number): string[] {
  return ['/', 'main', 'cms', 'events', 'setup', eventId.toString(), 'walls', 'list'];
}

export function getTeleprompterRoute(eventId: number): string[] {
  return ['/', 'teleprompter', eventId.toString()];
}

export function navigateToTeleprompter(eventId: number, agendaId?: number): [string[], object] {
  const queryParams: { agenda_id?: number } = {};
  if (agendaId) {
    queryParams.agenda_id = agendaId;
  }
  return [getTeleprompterRoute(eventId), Object.keys(queryParams).length ? { queryParams } : {}];
}

export function getCmsNewsItemsRoute(): string[] {
  return ['/', 'main', 'cms', 'news_items', 'list'];
}

export function getCmsNewsItemEditRoute(newsItemId: number): string[] {
  return ['/', 'main', 'cms', 'news_items', 'edit', newsItemId.toString()];
}

export function getCmsSubscriptionsRoute(): string[] {
  return ['/', 'main', 'cms', 'subscriptions', 'list'];
}

export function getCmsSubscriptionEditRoute(subscriptionId: number): string[] {
  return ['/', 'main', 'cms', 'subscriptions', 'edit', subscriptionId.toString()];
}

export function getCrmUserSubscriptionListRoute(userId: number): string[] {
  return ['/', 'main', 'crm', 'users', userId.toString(), 'user_subscriptions', 'list'];
}

/**
 * Validator to prevent certain hosts in URLs
 * Blocks:
 * - https://oak-depot.fra1.cdn.digitaloceanspaces.com
 * - https://s3.oak-events.com
 */
export function restrictedUrlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Don't validate empty values
    }

    const restrictedHosts = [
      'oak-depot.fra1.cdn.digitaloceanspaces.com',
      's3.oak-events.com'
    ];

    try {
      const url = new URL(control.value);
      const hostname = url.hostname.toLowerCase();
      
      if (restrictedHosts.includes(hostname)) {
        return { restrictedUrl: { host: hostname } };
      }
    } catch {
      // If it's not a valid URL, let other validators handle it
      return null;
    }

    return null;
  };
}
